import { cloneDeep } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React, { Component } from 'react';

import {
  BACKEND_DATE_FORMAT,
  INPUT_DATE_FORMAT,
  MINIMUM_ADULT_AGE,
  MINIMUM_DATE,
  UK_DIAL_CODE,
} from '../../../config/helpers';
import REGEX from '../../../config/regex';
import { imagePropTypes } from '../../../propTypes';
import { getErrorMessageKey } from '../../../util/errorCodes';
import jsonPost from '../../../util/jsonPost';
import {
  findCurrencyItemFromListWithFallback,
  getInitialCurrencyState,
} from '../../../util/currency';

import Button from '../Atoms/Button';
import MemberAddress from '../BookingFieldset/MemberAddress';
import MemberDetails from '../BookingFieldset/MemberDetails';
import { leadBookerPropTypes } from '../BookingFieldset/PropTypes';
import {
  initFieldsetProps,
  markRequiredFields,
  preventKeyCode,
  updateField,
  validateFieldset,
} from '../BookingFieldset/helpers';
import UpgradeDropdown from '../BookingUpgrades/UpgradeDropdown';
import { withErrorBoundary } from '../Core/ErrorBoundary';
import ErrorMessage from '../Core/ErrorMessage';
import PackageSummary from '../PackageSummary';
import StickyComponent from '../StickyComponent';
import CUSTOM_EVENT from '../../../util/customEvents';

const propTypes = exact({
  apiURL: PropTypes.string.isRequired,
  data: PropTypes.shape({
    paymentUser: leadBookerPropTypes,
    papQuantity: PropTypes.number,
    totalAmount: PropTypes.number,
    papOption: PropTypes.shape({
      costPerPerson: PropTypes.number,
      description: PropTypes.string,
      isCallToBook: PropTypes.bool,
      isSoldOut: PropTypes.bool,
      key: PropTypes.string,
      maxPartySize: PropTypes.number,
      title: PropTypes.string,
    }),
    pricePerCurrency: PropTypes.arrayOf(
      PropTypes.shape({
        current: PropTypes.string,
        totalPrice: PropTypes.number,
      })
    ),
    errorCode: PropTypes.number,
    errorMessage: PropTypes.string,
  }),
  labels: PropTypes.shape({
    addressFieldsetTitle: PropTypes.string,
    callToBookText: PropTypes.string,
    failedToRetrieveCheckoutProcessSettingsMessage: PropTypes.string,
    findAddressButtonText: PropTypes.string,
    invalidPaymentOptionMessage: PropTypes.string,
    invalidPriorityAccessPassIdErrorMessage: PropTypes.string,
    leadPartyMemberFieldsetSubTitle: PropTypes.string,
    leadPartyMemberFieldsetTitle: PropTypes.string,
    papSelectionLabel: PropTypes.string,
    preferredCardImage: imagePropTypes,
    submitButtonText: PropTypes.string,
    titleOptions: PropTypes.arrayOf(PropTypes.string),
    totalPriceText: PropTypes.string,
    placeholderTexts: PropTypes.shape({
      titlePlaceholderText: PropTypes.string,
      forenamePlaceholderText: PropTypes.string,
      surnamePlaceholderText: PropTypes.string,
      dateOfBirthPlaceholderText: PropTypes.string,
      validationReviewFields: PropTypes.string,
      validationDateOfBirthFormat: PropTypes.string,
      validationLeadMinimumAge: PropTypes.string,
      validationDateOfBirthInvalid: PropTypes.string,
      postcodePlaceholderText: PropTypes.string,
      addressLine1PlaceholderText: PropTypes.string,
      addressLine2PlaceholderText: PropTypes.string,
      cityPlaceholderText: PropTypes.string,
      telephonePlaceholderText: PropTypes.string,
      mobilePhonePlaceholderText: PropTypes.string,
      emailAddressPlaceholderText: PropTypes.string,
    }),
    validationReviewFields: PropTypes.string,
    validationDateOfBirthFormat: PropTypes.string,
    validationLeadMinimumAge: PropTypes.string,
    validationDateOfBirthInvalid: PropTypes.string,
  }),
  pcaPredictKey: PropTypes.string,
});

const defaultProps = {
  data: undefined,
  labels: undefined,
  pcaPredictKey: null,
};

const requiredFields = [
  'address.addressLine1',
  'address.city',
  'address.postcode',
  'dateOfBirth',
  'emailAddress',
  'fullName.forename',
  'fullName.prefix',
  'fullName.surname',
  'mobile',
];

class PapBookingDetails extends Component {
  constructor(props) {
    super(props);

    this.handleAddressPopulate = this.handleAddressPopulate.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFieldChange = this.handleFieldChange.bind(this);
    this.handlePapChange = this.handlePapChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleResetState = this.handleResetState.bind(this);

    const { data } = this.props;

    let paymentUser = data.paymentUser;

    paymentUser = initFieldsetProps(paymentUser);
    markRequiredFields(paymentUser, requiredFields);

    paymentUser.dateOfBirth.regex = REGEX.date;
    paymentUser.emailAddress.regex = REGEX.email;
    paymentUser.mobile.regex = REGEX.mobile;
    paymentUser.phone.regex = REGEX.phone;

    // Set united kingdom country dial code to default if empty string is given
    if (!paymentUser.phoneCountryDialCode.value) {
      paymentUser.phoneCountryDialCode.value = UK_DIAL_CODE;
    }

    // Set united kingdom country dial code to default if empty string is given
    if (!paymentUser.mobileCountryDialCode.value) {
      paymentUser.mobileCountryDialCode.value = UK_DIAL_CODE;
    }

    const currentCurrency = getInitialCurrencyState(data.pricePerCurrency);

    const currentPrice = findCurrencyItemFromListWithFallback(
      data.pricePerCurrency,
      currentCurrency.code
    );

    this.state = {
      errorMessage: null,
      isLoading: false,
      paymentUser,
      papQuantity: data.papQuantity,
      price: currentPrice.totalPrice,
      isCallToBook: false,
      currentCurrency,
      selectedPap: data.papOption,
    };
  }

  validateForm() {
    let formIsInvalid = false;

    const { paymentUser } = this.state;
    const { labels } = this.props;

    // Check required fields & regex for lead party member
    const paymentUserIsInvalid = validateFieldset(paymentUser);

    if (paymentUserIsInvalid) formIsInvalid = true;

    // Check lead party member DOB format
    const invalidDateOfBirthFormat = !moment(
      paymentUser.dateOfBirth.value,
      INPUT_DATE_FORMAT,
      true
    ).isValid();

    // Check lead party member is over 18
    let paymentUserIsUnderMinAge = false;
    let dateOfBirthBeforeMinDate = false;

    if (!formIsInvalid) {
      // Check DOB is after 1900 and LPM is older than 18
      const paymentUserDateOfBirth = moment(paymentUser.dateOfBirth.value, INPUT_DATE_FORMAT);
      paymentUserIsUnderMinAge = moment().diff(paymentUserDateOfBirth, 'years') < MINIMUM_ADULT_AGE;
      dateOfBirthBeforeMinDate = moment(MINIMUM_DATE).isAfter(paymentUserDateOfBirth);
      paymentUser.dateOfBirth.isInvalid = paymentUserIsUnderMinAge || dateOfBirthBeforeMinDate;
    }

    // Build error message
    let errorMessage = null;
    if (formIsInvalid) errorMessage = labels.validationReviewFields;
    else if (invalidDateOfBirthFormat)
      errorMessage = labels.validationDateOfBirthFormat.replace(
        '[INPUT_DATE_FORMAT]',
        `${INPUT_DATE_FORMAT}`
      );
    else if (paymentUserIsUnderMinAge) errorMessage = labels.validationLeadMinimumAge;
    else if (dateOfBirthBeforeMinDate) errorMessage = labels.validationDateOfBirthInvalid;

    // Update state & return if form is invalid
    this.setState({ errorMessage, paymentUser });
    return (
      formIsInvalid ||
      invalidDateOfBirthFormat ||
      paymentUserIsUnderMinAge ||
      dateOfBirthBeforeMinDate
    );
  }

  componentDidMount() {
    window.addEventListener('pageshow', this.handleResetState);
    window.addEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, this.handleCurrencyChange);
  }

  componentWillUnmount() {
    window.removeEventListener('pageshow', this.handleResetState);
    window.removeEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, this.handleCurrencyChange);
  }

  handleCurrencyChange = (event) => {
    const userSelectedCurrency = event?.detail;
    const { data } = this.props;

    // Find the now selected currency in our currency list from the BE
    const newCurrency = findCurrencyItemFromListWithFallback(
      data.pricePerCurrency,
      userSelectedCurrency.code
    );

    this.setState((prevState) => ({
      price: newCurrency.totalPrice * prevState.papQuantity,
      currentCurrency: userSelectedCurrency,
    }));
  };

  handleResetState() {
    this.setState({ isLoading: false });
  }

  handlePapChange(event) {
    const { data } = this.props;

    const mutableState = cloneDeep(this.state);
    const papQuantity = parseInt(event.target.value, 10);

    if (mutableState.papQuantity === papQuantity) return;

    const isCallToBook = papQuantity > data.papOption.maxPartySize;

    // Find the price per person for the currently selected currency
    const foundPriceOption =
      this.state.selectedPap?.pricePerPersonPerCurrency?.find(
        (papPriceOption) => papPriceOption.currency === this.state.currentCurrency?.code
      ) ?? {};

    // Store party size and update price
    mutableState.papQuantity = papQuantity;
    mutableState.price = foundPriceOption.totalPrice * papQuantity;
    mutableState.isCallToBook = isCallToBook;

    this.setState(mutableState);
  }

  handleBlur(event) {
    const newState = updateField(this.state, event.target.name, event.target.value, ({ value }) => {
      let newValue = value;

      if (typeof value === 'string') {
        newValue = value.trim();
      }

      return newValue;
    });

    this.setState(newState);
  }

  handleFieldChange(event) {
    const newState = updateField(
      this.state,
      event.target.name,
      event.target.value,
      ({ name, value, currentValue }) => {
        let newValue = value;

        if (name.split('.').pop() === 'dateOfBirth') {
          if (newValue.length > currentValue.value.length) {
            if (newValue.length === 2 || newValue.length === 5) newValue += '/';
          }
        }

        return newValue;
      }
    );

    this.setState(newState);
  }

  handleAddressPopulate(address) {
    const { paymentUser } = this.state;

    paymentUser.address = address;

    this.setState({ paymentUser });
  }

  handleSubmit(event) {
    event.preventDefault();

    const formIsInvalid = this.validateForm();
    if (formIsInvalid) return;

    this.setState({
      errorMessage: null,
      isLoading: true,
    });

    const { apiURL } = this.props;
    const { papQuantity, paymentUser } = this.state;

    const options = {
      paymentUser: {
        address: {
          addressLine1: paymentUser.address.addressLine1.value,
          addressLine2: paymentUser.address.addressLine2.value,
          city: paymentUser.address.city.value,
          county: paymentUser.address.county.value,
          postcode: paymentUser.address.postcode.value,
        },
        dateOfBirth: moment(paymentUser.dateOfBirth.value, INPUT_DATE_FORMAT).format(
          BACKEND_DATE_FORMAT
        ),
        emailAddress: paymentUser.emailAddress.value,
        fullName: {
          forename: paymentUser.fullName.forename.value,
          prefix: paymentUser.fullName.prefix.value,
          surname: paymentUser.fullName.surname.value,
        },
        mobile: paymentUser.mobile.value,
        mobileCountryDialCode: paymentUser.mobileCountryDialCode.value,
        phone: paymentUser.phone.value,
        phoneCountryDialCode: paymentUser.phoneCountryDialCode.value,
      },
      papQuantity,
    };

    jsonPost(apiURL, options, (response) => {
      if (response.errorCode || response.errors) {
        const errorMessageKey = getErrorMessageKey(response.errorCode);
        return this.setState({
          errorMessage:
            this.props.labels[errorMessageKey] || response.message || 'An error occurred',
          isLoading: false,
        });
      }

      window.location.href = response.redirectUri;
      return this.setState({ errorMessage: null });
    });
  }

  render() {
    const { data, labels, pcaPredictKey } = this.props;
    const {
      errorMessage,
      isLoading,
      isCallToBook,
      papQuantity,
      paymentUser,
      price,
      currentCurrency,
    } = this.state;

    return (
      <section className="Booking">
        <form noValidate autoComplete="false" onSubmit={this.handleSubmit}>
          {/* Package summary */}
          <div className="Booking_group Booking_group-inset">
            <div className="Booking_content">
              <StickyComponent contained>
                <PackageSummary
                  callToBookText={labels.callToBookText}
                  currencySymbol={currentCurrency.symbol}
                  description={data.papOption.description}
                  isCallToBook={isCallToBook}
                  preferredCardImage={labels.preferredCardImage}
                  price={price}
                  title={data.papOption.title}
                  totalPriceText={labels.totalPriceText}
                />
              </StickyComponent>
            </div>
          </div>
          {/* Pap size */}
          <div className="Booking_group">
            <div className="Booking_content Booking_content-inset">
              <UpgradeDropdown
                handleChange={this.handlePapChange}
                labelText={labels.papSelectionLabel}
                max={data.papOption.maxPartySize}
                selectedValue={papQuantity}
                showBeyondMaxOption
              />
            </div>
          </div>
          {/* Lead party member */}
          <div className="Booking_group Booking_group-inset">
            <div className="Booking_content">
              <fieldset className="Booking_item">
                <div className="Booking_heading">
                  <h3 className="Booking_itemTitle">{labels.leadPartyMemberFieldsetTitle}</h3>
                  <h4 className="Booking_itemSubTitle">{labels.leadPartyMemberFieldsetSubTitle}</h4>
                </div>
                <MemberDetails
                  fieldNamePrefix="paymentUser"
                  fieldset={paymentUser}
                  handleDOBChange={this.handleFieldChange}
                  handleFieldChange={this.handleFieldChange}
                  handleKeyDown={preventKeyCode(191)}
                  titleOptions={labels.titleOptions}
                  titlePlaceholderText={labels.placeholderTexts.titlePlaceholderText}
                  forenamePlaceholderText={labels.placeholderTexts.forenamePlaceholderText}
                  surnamePlaceholderText={labels.placeholderTexts.surnamePlaceholderText}
                  dateOfBirthPlaceholderText={labels.placeholderTexts.dateOfBirthPlaceholderText}
                />
              </fieldset>
              <fieldset className="Booking_item">
                <div className="Booking_heading">
                  <h4 className="Booking_itemTitle Booking_itemTitle-small">
                    {labels.addressFieldsetTitle}
                  </h4>
                </div>
                <MemberAddress
                  apiKey={pcaPredictKey}
                  buttonText={labels.findAddressButtonText}
                  fieldNamePrefix="paymentUser"
                  fieldset={paymentUser}
                  handleAddressPopulate={this.handleAddressPopulate}
                  handleFieldChange={this.handleFieldChange}
                  postcodePlaceholderText={labels.placeholderTexts.postcodePlaceholderText}
                  addressLine1PlaceholderText={labels.placeholderTexts.addressLine1PlaceholderText}
                  addressLine2PlaceholderText={labels.placeholderTexts.addressLine2PlaceholderText}
                  cityPlaceholderText={labels.placeholderTexts.cityPlaceholderText}
                  telephonePlaceholderText={labels.placeholderTexts.telephonePlaceholderText}
                  mobilePhonePlaceholderText={labels.placeholderTexts.mobilePhonePlaceholderText}
                  emailAddressPlaceholderText={labels.placeholderTexts.emailAddressPlaceholderText}
                />
              </fieldset>
            </div>
          </div>
          {/* Submit */}
          <div className="Booking_group Booking_group-inset">
            <div className="Booking_content">
              {errorMessage && (
                <div className="Booking_item">
                  <ErrorMessage text={errorMessage} />
                </div>
              )}
              <div className="Booking_item Booking_item-center">
                {!isCallToBook && (
                  <Button
                    buttonType="submit"
                    handleClick={this.handleSubmit}
                    isLoading={isLoading}
                    modifier="fill"
                    text={labels.submitButtonText}
                  />
                )}
                {isCallToBook && <div className="Booking_callToBook">{labels.callToBookText}</div>}
              </div>
            </div>
          </div>
        </form>
      </section>
    );
  }
}

PapBookingDetails.propTypes = propTypes;
PapBookingDetails.defaultProps = defaultProps;

export default withErrorBoundary(PapBookingDetails);
